import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff, Loader2, Car, ArrowRight, KeyRound } from 'lucide-react'
import { toast } from 'sonner'

import { supabase } from '@/lib/supabase'
import { loginSchema, LoginFormData } from '@/lib/validations/auth'
import { useAuth } from '@/hooks/useAuth'
import { useSEO } from '@/hooks/useSEO'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'

export default function LoginPage() {
  useSEO({ title: 'Entrar' })

  const navigate = useNavigate()
  const { profile } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  // Estado para verificação de MFA (aal2)
  const [mfaStep, setMfaStep] = useState(false)
  const [mfaFactorId, setMfaFactorId] = useState<string>('')
  const [mfaCode, setMfaCode] = useState('')
  const [mfaLoading, setMfaLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const redirectByRole = (role: string) => {
    if (role === 'admin') navigate('/admin')
    else if (role === 'instructor') navigate('/instrutor/dashboard')
    else navigate('/aluno/dashboard')
  }

  // Redirect once AuthContext has the profile loaded (handles both fresh login
  // and revisiting /login while already authenticated).
  useEffect(() => {
    if (profile) {
      redirectByRole(profile.role)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile])

  const onSubmit = async (data: LoginFormData) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    })

    if (error) {
      toast.error('Erro ao entrar', {
        description:
          error.message === 'Invalid login credentials'
            ? 'E-mail ou senha incorretos.'
            : error.message,
      })
      return
    }

    // Verifica se o usuário tem MFA ativo e precisa subir para aal2
    const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
    if (aalData?.nextLevel === 'aal2' && aalData.nextLevel !== aalData.currentLevel) {
      // Busca o factor verificado para o challenge
      const { data: factors } = await supabase.auth.mfa.listFactors()
      const totpFactor = factors?.totp?.find((f) => f.status === 'verified')
      if (totpFactor) {
        setMfaFactorId(totpFactor.id)
        setMfaCode('')
        setMfaStep(true)
        return
      }
    }

    // AuthContext's onAuthStateChange will fetch the profile and set loading=true
    // until it completes. The useEffect above fires once profile is ready and
    // redirects to the correct dashboard — no manual fetch needed here.
    toast.success('Bem-vindo de volta!')
  }

  const handleMfaVerify = async () => {
    if (!/^\d{6}$/.test(mfaCode)) {
      toast.error('Digite os 6 dígitos do código.')
      return
    }

    setMfaLoading(true)
    try {
      const { data: challenge, error: challengeErr } =
        await supabase.auth.mfa.challenge({ factorId: mfaFactorId })
      if (challengeErr) throw challengeErr

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challenge.id,
        code: mfaCode,
      })
      if (verifyErr) throw verifyErr

      setMfaStep(false)
      toast.success('Bem-vindo de volta!')
      // O AuthContext vai detectar a sessão atualizada e redirecionar
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Código inválido.'
      toast.error(msg.includes('Invalid') ? 'Código incorreto. Tente novamente.' : msg)
    } finally {
      setMfaLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    if (error) {
      toast.error('Erro ao entrar com Google', { description: error.message })
      setIsGoogleLoading(false)
    }
  }

  const handleForgotPassword = async (email: string) => {
    if (!email) {
      toast.error('Digite seu e-mail primeiro para redefinir a senha.')
      return
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/redefinir-senha`,
    })
    if (error) {
      toast.error('Erro ao enviar e-mail', { description: error.message })
    } else {
      toast.success('E-mail enviado!', {
        description: 'Verifique sua caixa de entrada para redefinir a senha.',
      })
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 flex-col justify-between p-12 text-white">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 rounded-xl p-2">
            <Car className="h-7 w-7 text-white" />
          </div>
          <span className="text-2xl font-bold tracking-tight">MeuDriver</span>
        </div>

        <div className="space-y-6">
          <h1 className="text-4xl font-bold leading-tight">
            Aprenda a dirigir com os melhores instrutores do Brasil
          </h1>
          <p className="text-brand-100 text-lg leading-relaxed">
            Conectamos alunos a instrutores qualificados para aulas práticas de
            direção. Agende, pague com segurança e evolua no seu ritmo.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4">
            {[
              { value: '12k+', label: 'Alunos' },
              { value: '800+', label: 'Instrutores' },
              { value: '4.9★', label: 'Avaliação' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-white/10 rounded-xl p-4 text-center"
              >
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-brand-200 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-brand-300 text-sm">
          © {new Date().getFullYear()} MeuDriver. Todos os direitos reservados.
        </p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-gray-50">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 lg:hidden">
            <div className="bg-brand-600 rounded-xl p-2">
              <Car className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">MeuDriver</span>
          </div>

          {/* MFA Step */}
          {mfaStep ? (
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <KeyRound className="h-6 w-6 text-brand-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Verificacao em 2 etapas</h2>
                </div>
                <p className="text-gray-500 text-sm">
                  Abra seu app autenticador (Google Authenticator, Authy etc.) e insira o
                  codigo de 6 digitos para continuar.
                </p>
              </div>
              <div className="space-y-3">
                <Label htmlFor="mfa-code">Codigo TOTP</Label>
                <Input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-36 font-mono text-center text-xl tracking-widest h-12"
                  autoComplete="one-time-code"
                  autoFocus
                  data-testid="mfa-code-input"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={handleMfaVerify}
                  disabled={mfaLoading || mfaCode.length !== 6}
                  className="bg-brand-600 hover:bg-brand-700 gap-2"
                  data-testid="mfa-verify-btn"
                >
                  {mfaLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4" />
                  )}
                  {mfaLoading ? 'Verificando...' : 'Verificar'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setMfaStep(false); supabase.auth.signOut() }}
                  disabled={mfaLoading}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>

          <div>
            <h2 className="text-3xl font-bold text-gray-900">Bem-vindo!</h2>
            <p className="mt-2 text-gray-500">
              Faça login para continuar na plataforma.
            </p>
          </div>

          {/* Google button */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 text-base font-medium border-gray-300"
            onClick={handleGoogleLogin}
            disabled={isGoogleLoading}
          >
            {isGoogleLoading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            Entrar com Google
          </Button>

          {/* Divider */}
          <div className="relative">
            <Separator />
            <span className="absolute left-1/2 -translate-x-1/2 -top-3 bg-gray-50 px-3 text-sm text-gray-400">
              ou continue com e-mail
            </span>
          </div>

          {/* Login form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                autoComplete="email"
                className="h-11"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="h-11 pr-11"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-500">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(!!checked)}
                />
                <Label
                  htmlFor="remember"
                  className="text-sm font-normal cursor-pointer text-gray-600"
                >
                  Lembrar de mim
                </Label>
              </div>
              <ForgotPasswordButton register={register} onReset={handleForgotPassword} />
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold bg-brand-600 hover:bg-brand-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <ArrowRight className="h-5 w-5 mr-2" />
              )}
              {isSubmitting ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-500">
            Não tem uma conta?{' '}
            <Link
              to="/cadastro"
              className="font-semibold text-brand-600 hover:text-brand-700 hover:underline"
            >
              Cadastre-se grátis
            </Link>
          </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// Internal helper component to access form email value for forgot password
function ForgotPasswordButton({
  register,
  onReset,
}: {
  register: ReturnType<typeof useForm<LoginFormData>>['register']
  onReset: (email: string) => void
}) {
  const handleClick = () => {
    // Read the current value of the email input from the DOM
    const emailInput = document.getElementById('email') as HTMLInputElement | null
    onReset(emailInput?.value ?? '')
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="text-sm text-brand-600 hover:text-brand-700 hover:underline font-medium"
    >
      Esqueci minha senha
    </button>
  )
}
